package com.kanati.admin.features.users

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import com.kanati.admin.api.AdminApiClient
import com.kanati.admin.components.ActionButton
import com.kanati.admin.components.BannerTone
import com.kanati.admin.components.FieldRow
import com.kanati.admin.components.SectionCard
import com.kanati.admin.components.StatusBanner
import com.kanati.admin.components.TextField
import com.kanati.admin.state.MaterialThemePalette
import com.kanati.admin.state.SelectedDistrictContext
import com.kanati.shared.admin.AccessScope
import com.kanati.shared.admin.CreateOperatorRequest
import com.kanati.shared.admin.ManagedOperatorSummary
import com.kanati.shared.admin.OperatorAccessAssignmentRequest
import com.kanati.shared.admin.UpdateOperatorRequest
import com.kanati.shared.school.School
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.FlexDirection
import org.jetbrains.compose.web.css.borderRadius
import org.jetbrains.compose.web.css.display
import org.jetbrains.compose.web.css.flexDirection
import org.jetbrains.compose.web.css.gap
import org.jetbrains.compose.web.css.margin
import org.jetbrains.compose.web.css.padding
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H3
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Text

@Composable
fun UsersScreen(
    apiClient: AdminApiClient,
    district: SelectedDistrictContext?,
    schools: List<School>,
    districtLoading: Boolean,
    districtLoadError: String?,
    theme: MaterialThemePalette,
    canEdit: Boolean,
) {
    val scope = rememberCoroutineScope()
    var users by remember { mutableStateOf<List<ManagedOperatorSummary>>(emptyList()) }
    var isRefreshing by remember { mutableStateOf(false) }
    var isSubmitting by remember { mutableStateOf(false) }
    var loadError by remember { mutableStateOf<String?>(null) }
    var formError by remember { mutableStateOf<String?>(null) }
    var formSuccess by remember { mutableStateOf<String?>(null) }
    var editingOperatorId by remember { mutableStateOf<String?>(null) }
    var email by remember { mutableStateOf("") }
    var displayName by remember { mutableStateOf("") }
    var password by remember { mutableStateOf("") }
    var active by remember { mutableStateOf(true) }
    var accessScope by remember { mutableStateOf(AccessScope.DISTRICT) }
    var districtWide by remember { mutableStateOf(true) }
    var selectedAdminAccess by remember { mutableStateOf(true) }
    var selectedPosAccess by remember { mutableStateOf(false) }
    var selectedSchoolIds by remember { mutableStateOf<Set<String>>(emptySet()) }

    fun missingDistrictMessage(): String = when {
        districtLoading -> "Loading district context..."
        districtLoadError != null -> "District context could not be loaded. Refresh context and select a district."
        else -> "Select a district to manage users."
    }

    fun resetForm() {
        editingOperatorId = null
        email = ""
        displayName = ""
        password = ""
        active = true
        accessScope = AccessScope.DISTRICT
        districtWide = true
        selectedAdminAccess = true
        selectedPosAccess = false
        selectedSchoolIds = emptySet()
        formError = null
    }

    fun applyUserToForm(summary: ManagedOperatorSummary) {
        editingOperatorId = summary.operator.id
        email = summary.operator.email
        displayName = summary.operator.displayName
        password = ""
        active = summary.operator.active
        accessScope = summary.assignment.scope
        districtWide = summary.assignment.districtWide
        selectedAdminAccess = summary.assignment.adminAccess
        selectedPosAccess = summary.assignment.posAccess
        selectedSchoolIds = summary.assignment.schools.map { it.id }.toSet()
        formError = null
        formSuccess = null
    }

    fun buildAssignmentRequest(): OperatorAccessAssignmentRequest? {
        if (!selectedAdminAccess && !selectedPosAccess) {
            formError = "Select admin access, POS access, or both."
            return null
        }
        if (accessScope == AccessScope.DISTRICT && !districtWide && selectedSchoolIds.isEmpty()) {
            formError = "Choose at least one school when district access is restricted."
            return null
        }
        if (accessScope == AccessScope.SCHOOL && selectedSchoolIds.isEmpty()) {
            formError = "Choose at least one school for school-level access."
            return null
        }
        return OperatorAccessAssignmentRequest(
            scope = accessScope,
            adminAccess = selectedAdminAccess,
            posAccess = selectedPosAccess,
            districtWide = accessScope == AccessScope.DISTRICT && districtWide,
            schoolIds = if (accessScope == AccessScope.DISTRICT && districtWide) emptyList() else selectedSchoolIds.toList(),
        )
    }

    suspend fun refreshUsers(selectedDistrict: SelectedDistrictContext) {
        isRefreshing = true
        loadError = null
        try {
            users = apiClient.getUsers(selectedDistrict.id).sortedBy { it.operator.displayName.lowercase() }
        } catch (exception: Exception) {
            loadError = exception.message ?: "Unable to load users."
        } finally {
            isRefreshing = false
        }
    }

    LaunchedEffect(district?.id, districtLoading, districtLoadError) {
        val selectedDistrict = district
        resetForm()
        if (selectedDistrict != null) {
            refreshUsers(selectedDistrict)
        } else {
            users = emptyList()
            loadError = missingDistrictMessage()
        }
    }

    SectionCard(
        title = "Users",
        theme = theme,
        description = "Create and update district-level or school-level operator access using the simplified pilot roles: Admin and POS Operator.",
        headerActions = {
            ActionButton(
                label = if (isRefreshing) "Loading..." else "Refresh",
                onClick = {
                    val selectedDistrict = district ?: return@ActionButton
                    scope.launch { refreshUsers(selectedDistrict) }
                },
                theme = theme,
                isPrimary = false,
                isDisabled = district == null || isRefreshing,
            )
        },
    ) {
        if (district == null) {
            StatusBanner(
                message = missingDistrictMessage(),
                theme = theme,
                tone = if (districtLoadError != null) BannerTone.Error else BannerTone.Info,
            )
            return@SectionCard
        }

        if (!canEdit) {
            StatusBanner(
                message = "Read-only access. User creation and updates require district admin access.",
                theme = theme,
            )
        }

        loadError?.let { StatusBanner(message = it, tone = BannerTone.Error, theme = theme) }
        formError?.let { StatusBanner(message = it, tone = BannerTone.Error, theme = theme) }
        formSuccess?.let { StatusBanner(message = it, tone = BannerTone.Success, theme = theme) }

        H3({ style { margin(0.px) } }) {
            Text(if (editingOperatorId == null) "Create User" else "Edit User")
        }

        if (editingOperatorId == null) {
            FieldRow {
                TextField(
                    theme = theme,
                    label = "Email",
                    value = email,
                    placeholder = "operator@kanati.local",
                    onValueChange = { email = it },
                )
                TextField(
                    theme = theme,
                    label = "Display Name",
                    value = displayName,
                    placeholder = "Roosevelt Operator",
                    onValueChange = { displayName = it },
                )
            }
        } else {
            P({ style { margin(0.px); property("color", theme.onSurfaceVariant) } }) {
                Text("Editing $email")
            }
            FieldRow {
                TextField(
                    theme = theme,
                    label = "Display Name",
                    value = displayName,
                    placeholder = "Roosevelt Operator",
                    onValueChange = { displayName = it },
                )
            }
        }

        FieldRow {
            TextField(
                theme = theme,
                label = if (editingOperatorId == null) "Password" else "New Password",
                value = password,
                placeholder = if (editingOperatorId == null) "Temporary password" else "Leave blank to keep current password",
                onValueChange = { password = it },
            )
        }

        Div(
            attrs = {
                style {
                    display(DisplayStyle.Flex)
                    gap(10.px)
                    property("flex-wrap", "wrap")
                }
            }
        ) {
            ScopeChip(
                label = if (active) "User Active" else "User Inactive",
                isSelected = active,
                onClick = { if (canEdit) active = !active },
                theme = theme,
            )
            ScopeChip(
                label = "District-level",
                isSelected = accessScope == AccessScope.DISTRICT,
                onClick = {
                    if (canEdit) {
                        accessScope = AccessScope.DISTRICT
                        districtWide = true
                    }
                },
                theme = theme,
            )
            ScopeChip(
                label = "School-level",
                isSelected = accessScope == AccessScope.SCHOOL,
                onClick = {
                    if (canEdit) {
                        accessScope = AccessScope.SCHOOL
                        districtWide = false
                    }
                },
                theme = theme,
            )
        }

        AccessSelector(
            adminAccess = selectedAdminAccess,
            posAccess = selectedPosAccess,
            onToggleAdmin = {
                if (!canEdit) return@AccessSelector
                selectedAdminAccess = !selectedAdminAccess
            },
            onTogglePos = {
                if (!canEdit) return@AccessSelector
                selectedPosAccess = !selectedPosAccess
            },
            theme = theme,
        )

        if (accessScope == AccessScope.DISTRICT) {
            ScopeChip(
                label = if (districtWide) "District-wide access" else "Selected schools only",
                isSelected = districtWide,
                onClick = { if (canEdit) districtWide = !districtWide },
                theme = theme,
            )
        }

        if (accessScope == AccessScope.SCHOOL || !districtWide) {
            SchoolSelectionGrid(
                schools = schools,
                selectedSchoolIds = selectedSchoolIds,
                onToggle = { schoolId ->
                    if (!canEdit) return@SchoolSelectionGrid
                    selectedSchoolIds = if (schoolId in selectedSchoolIds) {
                        selectedSchoolIds - schoolId
                    } else {
                        selectedSchoolIds + schoolId
                    }
                },
                theme = theme,
            )
        }

        Div(
            attrs = {
                style {
                    display(DisplayStyle.Flex)
                    gap(10.px)
                    property("flex-wrap", "wrap")
                }
            }
        ) {
            ActionButton(
                label = if (isSubmitting) "Saving..." else if (editingOperatorId == null) "Create User" else "Save User",
                onClick = {
                    val selectedDistrict = district
                    val assignment = buildAssignmentRequest()
                    if (!canEdit || selectedDistrict == null || assignment == null) return@ActionButton
                    when {
                        displayName.isBlank() -> formError = "Display name is required."
                        editingOperatorId == null && email.isBlank() -> formError = "Email is required."
                        editingOperatorId == null && password.isBlank() -> formError = "Password is required."
                        else -> scope.launch {
                            isSubmitting = true
                            formError = null
                            formSuccess = null
                            try {
                                if (editingOperatorId == null) {
                                    apiClient.createUser(
                                        CreateOperatorRequest(
                                            districtId = selectedDistrict.id,
                                            email = email.trim(),
                                            displayName = displayName.trim(),
                                            password = password,
                                            active = active,
                                            assignment = assignment,
                                        )
                                    )
                                    formSuccess = "User created."
                                } else {
                                    apiClient.updateUser(
                                        operatorId = editingOperatorId!!,
                                        request = UpdateOperatorRequest(
                                            districtId = selectedDistrict.id,
                                            displayName = displayName.trim(),
                                            password = password.ifBlank { null },
                                            active = active,
                                            assignment = assignment,
                                        )
                                    )
                                    formSuccess = "User updated."
                                }
                                resetForm()
                                refreshUsers(selectedDistrict)
                            } catch (exception: Exception) {
                                formError = exception.message ?: "Unable to save user."
                            } finally {
                                isSubmitting = false
                            }
                        }
                    }
                },
                theme = theme,
                isDisabled = isSubmitting || !canEdit,
            )
            if (editingOperatorId != null) {
                ActionButton(
                    label = "Cancel Edit",
                    onClick = ::resetForm,
                    theme = theme,
                    isPrimary = false,
                    isDisabled = isSubmitting,
                )
            }
        }

        if (users.isEmpty() && !isRefreshing) {
            StatusBanner(
                message = "No district-scoped or school-scoped users are configured for ${district.name} yet.",
                theme = theme,
            )
        }

        users.forEach { user ->
            Div(
                attrs = {
                    style {
                        display(DisplayStyle.Flex)
                        flexDirection(FlexDirection.Column)
                        gap(10.px)
                        padding(18.px)
                        borderRadius(24.px)
                        property(
                            "background",
                            "linear-gradient(180deg, ${theme.surfaceContainerHighest} 0%, ${theme.surfaceVariant} 100%)"
                        )
                        property("border", "1px solid ${theme.outlineVariant}")
                        property("box-shadow", "inset 0 1px 0 ${theme.scrim}")
                    }
                }
            ) {
                Div(
                    attrs = {
                        style {
                            display(DisplayStyle.Flex)
                            property("justify-content", "space-between")
                            property("align-items", "start")
                            property("gap", "12px")
                            property("flex-wrap", "wrap")
                        }
                    }
                ) {
                    Div {
                        P({ style { margin(0.px); property("font-weight", "700") } }) {
                            Text(user.operator.displayName)
                        }
                        P({ style { margin(0.px); property("color", theme.onSurfaceVariant) } }) {
                            Text(user.operator.email)
                        }
                    }
                    ActionButton(
                        label = "Edit",
                        onClick = { applyUserToForm(user) },
                        theme = theme,
                        isPrimary = false,
                        isDisabled = !canEdit,
                    )
                }

                P({ style { margin(0.px); property("color", theme.onSurfaceVariant) } }) {
                    Text(userSummary(user))
                }
            }
        }
    }
}

@Composable
private fun AccessSelector(
    adminAccess: Boolean,
    posAccess: Boolean,
    onToggleAdmin: () -> Unit,
    onTogglePos: () -> Unit,
    theme: MaterialThemePalette,
) {
    Div(
        attrs = {
            style {
                display(DisplayStyle.Flex)
                gap(10.px)
                property("flex-wrap", "wrap")
            }
        }
    ) {
        PermissionChip(
            label = "Admin Access",
            isSelected = adminAccess,
            onClick = onToggleAdmin,
            theme = theme,
        )
        PermissionChip(
            label = "POS Access",
            isSelected = posAccess,
            onClick = onTogglePos,
            theme = theme,
        )
    }
}

@Composable
private fun SchoolSelectionGrid(
    schools: List<School>,
    selectedSchoolIds: Set<String>,
    onToggle: (String) -> Unit,
    theme: MaterialThemePalette,
) {
    Div(
        attrs = {
            style {
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                gap(8.px)
            }
        }
    ) {
        P({ style { margin(0.px); property("color", theme.onSurfaceVariant) } }) {
            Text("Allowed schools")
        }
        Div(
            attrs = {
                style {
                    display(DisplayStyle.Flex)
                    gap(10.px)
                    property("flex-wrap", "wrap")
                }
            }
        ) {
            schools.forEach { school ->
                ScopeChip(
                    label = school.shortName?.takeIf(String::isNotBlank) ?: school.name,
                    isSelected = school.id in selectedSchoolIds,
                    onClick = { onToggle(school.id) },
                    theme = theme,
                )
            }
        }
    }
}

@Composable
private fun PermissionChip(
    label: String,
    isSelected: Boolean,
    onClick: () -> Unit,
    theme: MaterialThemePalette,
) {
    ScopeChip(label = label, isSelected = isSelected, onClick = onClick, theme = theme)
}

@Composable
private fun ScopeChip(
    label: String,
    isSelected: Boolean,
    onClick: () -> Unit,
    theme: MaterialThemePalette,
) {
    Button(
        attrs = {
            onClick { onClick() }
            style {
                padding(10.px, 14.px)
                borderRadius(999.px)
                property(
                    "background",
                    if (isSelected) theme.primaryContainer else theme.surfaceContainerHighest
                )
                property("color", if (isSelected) theme.onPrimaryContainer else theme.onSurfaceVariant)
                property("border", "1px solid ${theme.outlineVariant}")
                property("font-weight", "700")
                property("cursor", "pointer")
            }
        }
    ) {
        Text(label)
    }
}

private fun userSummary(user: ManagedOperatorSummary): String {
    val scopeLabel = when (user.assignment.scope) {
        AccessScope.DISTRICT -> if (user.assignment.districtWide) {
            "District-wide"
        } else {
            "District access to ${user.assignment.schools.joinToString { it.shortName ?: it.name }}"
        }
        AccessScope.SCHOOL -> "School access to ${user.assignment.schools.joinToString { it.shortName ?: it.name }}"
    }
    val accessLabels = buildList {
        if (user.assignment.adminAccess) add("Admin")
        if (user.assignment.posAccess) add("POS")
    }.joinToString(" + ")
    val status = if (user.operator.active) "Active" else "Inactive"
    return "$status • $scopeLabel • $accessLabels"
}
